/**
 * Wo steht ein Projekt nach dem Angebot? (Wunsch von Mathias, 11/2026)
 *
 * Auf der Übersicht soll man sehen: Ist das Angebot draußen, ist der Auftrag
 * da, was ist als Nächstes offen — mit Zeitstempel und wer es war — und woran
 * erinnert werden muss: Netzzugangsvertrag, Stromliefervertrag,
 * Förderansuchen, Fertigmeldung beim Netzbetreiber.
 *
 * Gespeichert ist je Schritt nur, was jemand getan hat (`projektschritte`).
 * Angebot, Auftrag und Abnahme stehen ohnehin schon im Projekt; fehlt ihre
 * Zeile, werden sie daraus abgeleitet — so stimmt die Übersicht auch für
 * Projekte, die älter sind als diese Liste. Der Zustand selbst wird nie
 * gespeichert, sondern bei jeder Anzeige gerechnet, wie das Raster.
 */

import { tagVon } from './datum.ts';
import type {
  Angebotszeile,
  Auftragszeile,
  Dokument,
  Dokumentart,
  Projektschritt,
  Schrittart,
} from './typen.ts';

export interface Erinnerungsvorgabe {
  netzzugangsvertragTage: number;
  stromliefervertragTage: number;
  foerderansuchenTage: number;
  fertigmeldungTage: number;
}

interface Schrittdefinition {
  schritt: Schrittart;
  titel: string;
  /** Erst relevant, wenn dieser Schritt erledigt ist. */
  nach?: Schrittart;
  /** Nach wie vielen Tagen ab `nach` erinnert wird. */
  frist?: keyof Erinnerungsvorgabe;
  /** Welche Dokumentart hierher gehört. */
  dokument?: Dokumentart;
  /** Ohne diese Dokumentart lässt sich der Schritt nicht abschließen. */
  braucht?: { art: Dokumentart; text: string };
}

export const SCHRITTE: Schrittdefinition[] = [
  { schritt: 'angebot', titel: 'Angebot versendet' },
  { schritt: 'auftrag', titel: 'Auftrag erhalten' },
  {
    schritt: 'netzzugangsvertrag',
    titel: 'Netzzugangsvertrag',
    nach: 'auftrag',
    frist: 'netzzugangsvertragTage',
    dokument: 'netzzugangsvertrag',
  },
  {
    schritt: 'stromliefervertrag',
    titel: 'Stromliefervertrag',
    nach: 'auftrag',
    frist: 'stromliefervertragTage',
    dokument: 'stromliefervertrag',
  },
  {
    schritt: 'foerderansuchen',
    titel: 'Förderansuchen',
    nach: 'auftrag',
    frist: 'foerderansuchenTage',
    dokument: 'foerderansuchen',
  },
  { schritt: 'montage', titel: 'Montage abgenommen', nach: 'auftrag' },
  {
    schritt: 'fertigmeldung',
    titel: 'Fertigmeldung beim Netzbetreiber',
    nach: 'montage',
    frist: 'fertigmeldungTage',
    dokument: 'fertigmeldung',
    braucht: { art: 'wechselrichter', text: 'Datei des Wechselrichters fehlt' },
  },
];

export const SCHRITTNAMEN: Record<Schrittart, string> = Object.fromEntries(
  SCHRITTE.map((s) => [s.schritt, s.titel]),
) as Record<Schrittart, string>;

export const DOKUMENTARTEN: Array<[Dokumentart, string]> = [
  ['netzzugangsvertrag', 'Netzzugangsvertrag'],
  ['stromliefervertrag', 'Stromliefervertrag'],
  ['foerderansuchen', 'Förderansuchen'],
  ['wechselrichter', 'Datei des Wechselrichters'],
  ['fertigmeldung', 'Fertigmeldung'],
  ['sonstiges', 'Sonstiges'],
];

export type Schrittzustand = 'erledigt' | 'entfaellt' | 'offen' | 'faellig' | 'wartet';

export interface Schrittstand {
  schritt: Schrittart;
  titel: string;
  zustand: Schrittzustand;
  /** Erledigt am — ISO-Zeitpunkt. */
  erledigtAm: string | null;
  erledigtVon: string | null;
  /** Von Hand abgehakt oder aus Angebot, Auftrag, Abnahme abgeleitet. */
  quelle: 'hand' | 'abgeleitet' | null;
  /** Tag der Erinnerung, JJJJ-MM-TT — gesetzt oder aus der Vorgabe. */
  erinnernAm: string | null;
  erinnerungGesetzt: boolean;
  /** Wie viele Tage über der Erinnerung, ab 0. */
  tageUeber: number | null;
  /** Was noch fehlt, um abzuschließen. */
  fehlt: string | null;
  dokument?: Dokumentart;
  zeile: Projektschritt | null;
}

const tag = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function plusTage(iso: string, tage: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + tage);
  return tag(d);
}

function tageZwischen(von: string, bis: string): number {
  const a = new Date(`${von}T12:00:00`);
  const b = new Date(`${bis}T12:00:00`);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export interface Abwicklungseingabe {
  angebote: Angebotszeile[];
  auftraege: Auftragszeile[];
  schritte: Projektschritt[];
  dokumente: Dokument[];
  vorgabe: Erinnerungsvorgabe;
  heute: Date;
}

/** Was sich aus dem Projekt ablesen lässt, wenn niemand den Schritt abgehakt hat. */
function abgeleitet(
  schritt: Schrittart,
  e: Abwicklungseingabe,
): { am: string | null; von: string | null } | null {
  const angebote = e.angebote.filter((a) => a.status !== 'entwurf');
  // Der älteste Auftrag zuerst. `geaendert_am` wandert mit jeder Änderung
  // an der Checkliste — als Datum des Auftrags taugt es nur, wenn sonst
  // nichts da ist.
  const seit = (a: Auftragszeile): string =>
    ((a as Auftragszeile & { angelegt_am?: string }).angelegt_am ?? a.geaendert_am ?? '');
  const auftraege = e.auftraege.filter((a) => !a.geloescht_am).sort((a, b) => seit(a).localeCompare(seit(b)));
  if (schritt === 'angebot' && angebote.length > 0) {
    const erstes = [...angebote].sort((a, b) => (tagVon(a.datum) ?? '').localeCompare(tagVon(b.datum) ?? ''))[0]!;
    return { am: tagVon(erstes.datum), von: null };
  }
  if (schritt === 'auftrag') {
    const angenommen = e.angebote.find((a) => a.status === 'angenommen');
    if (auftraege.length > 0) {
      const erster = auftraege[0]! as Auftragszeile & { angelegt_am?: string };
      return { am: erster.angelegt_am ?? tagVon(angenommen?.datum) ?? erster.geaendert_am ?? null, von: null };
    }
    if (angenommen) return { am: tagVon(angenommen.datum), von: null };
  }
  if (schritt === 'montage') {
    const abgenommen = auftraege.find((a) => a.abgenommen_am || a.status === 'abgenommen');
    if (abgenommen) {
      return { am: abgenommen.abgenommen_am ?? tagVon(abgenommen.montage_bis) ?? abgenommen.geaendert_am ?? null, von: abgenommen.monteur };
    }
  }
  return null;
}

export function schrittstaende(e: Abwicklungseingabe): Schrittstand[] {
  const heute = tag(e.heute);
  const stand = new Map<Schrittart, Schrittstand>();
  for (const def of SCHRITTE) {
    const zeile = e.schritte.find((s) => s.schritt === def.schritt && !s.geloescht_am) ?? null;
    const basis = {
      schritt: def.schritt,
      titel: def.titel,
      erinnerungGesetzt: !!zeile?.erinnern_am,
      zeile,
      ...(def.dokument ? { dokument: def.dokument } : {}),
    };
    const fehlt =
      def.braucht && !e.dokumente.some((d) => d.art === def.braucht!.art && !d.geloescht_am) ? def.braucht.text : null;

    if (zeile?.entfaellt) {
      stand.set(def.schritt, { ...basis, zustand: 'entfaellt', erledigtAm: zeile.erledigt_am, erledigtVon: zeile.erledigt_von, quelle: 'hand', erinnernAm: null, tageUeber: null, fehlt: null });
      continue;
    }
    if (zeile?.erledigt_am) {
      stand.set(def.schritt, { ...basis, zustand: 'erledigt', erledigtAm: zeile.erledigt_am, erledigtVon: zeile.erledigt_von, quelle: 'hand', erinnernAm: null, tageUeber: null, fehlt: null });
      continue;
    }
    const aus = abgeleitet(def.schritt, e);
    if (aus) {
      stand.set(def.schritt, { ...basis, zustand: 'erledigt', erledigtAm: aus.am, erledigtVon: aus.von, quelle: 'abgeleitet', erinnernAm: null, tageUeber: null, fehlt: null });
      continue;
    }

    // Offen. Relevant erst, wenn der auslösende Schritt durch ist.
    const vorher = def.nach ? stand.get(def.nach) : undefined;
    const ausgeloest = !def.nach || vorher?.zustand === 'erledigt' || vorher?.zustand === 'entfaellt';
    if (!ausgeloest) {
      stand.set(def.schritt, { ...basis, zustand: 'wartet', erledigtAm: null, erledigtVon: null, quelle: null, erinnernAm: zeile?.erinnern_am ?? null, tageUeber: null, fehlt });
      continue;
    }
    const erinnernAm =
      tagVon(zeile?.erinnern_am) ??
      (def.frist && vorher?.erledigtAm ? plusTage(vorher.erledigtAm, e.vorgabe[def.frist]) : null);
    const ueber = erinnernAm ? tageZwischen(erinnernAm, heute) : null;
    stand.set(def.schritt, {
      ...basis,
      zustand: ueber !== null && ueber >= 0 ? 'faellig' : 'offen',
      erledigtAm: null,
      erledigtVon: null,
      quelle: null,
      erinnernAm,
      tageUeber: ueber !== null && ueber >= 0 ? ueber : null,
      fehlt,
    });
  }
  // Ein Auftrag ohne versendetes Angebot (mündlich, telefonisch) ist kein
  // offenes Angebot mehr — sonst stünde „Angebot versendet“ als nächster
  // Schritt vor einem Projekt, das längst in der Abwicklung ist.
  const angebot = stand.get('angebot')!;
  const auftrag = stand.get('auftrag')!;
  if ((angebot.zustand === 'offen' || angebot.zustand === 'faellig') && auftrag.zustand === 'erledigt') {
    stand.set('angebot', { ...angebot, zustand: 'entfaellt', erinnernAm: null, tageUeber: null });
  }
  return SCHRITTE.map((d) => stand.get(d.schritt)!);
}

export interface Projektlage {
  angebotVersendet: Schrittstand;
  auftragErhalten: Schrittstand;
  /** Der erste offene Schritt in der Reihenfolge — nicht der fälligste. */
  naechster: Schrittstand | null;
  faellig: Schrittstand[];
  /** Was zuletzt erledigt wurde, mit Zeitpunkt — „zuletzt: …“. */
  zuletzt: Schrittstand | null;
  erledigt: number;
  gesamt: number;
}

export function projektlage(staende: Schrittstand[]): Projektlage {
  const nach = (s: Schrittart): Schrittstand => staende.find((x) => x.schritt === s)!;
  const erledigte = staende.filter((s) => s.zustand === 'erledigt' && s.erledigtAm);
  return {
    angebotVersendet: nach('angebot'),
    auftragErhalten: nach('auftrag'),
    naechster: staende.find((s) => s.zustand === 'offen' || s.zustand === 'faellig') ?? null,
    faellig: staende.filter((s) => s.zustand === 'faellig').sort((a, b) => (b.tageUeber ?? 0) - (a.tageUeber ?? 0)),
    zuletzt: [...erledigte].sort((a, b) => b.erledigtAm!.localeCompare(a.erledigtAm!))[0] ?? null,
    erledigt: staende.filter((s) => s.zustand === 'erledigt' || s.zustand === 'entfaellt').length,
    gesamt: staende.length,
  };
}

/**
 * Die Kennung einer Schrittzeile, aus Projekt und Schritt gebildet. Legen zwei
 * Geräte ohne Verbindung denselben Schritt an, treffen sie dieselbe Zeile —
 * der Abgleich meldet dann einen Konflikt, statt zwei Zeilen zu führen. Die
 * dritte Gruppe beginnt mit „a“, eine zufällige Kennung (Version 4) dort mit
 * „4“ — die beiden können sich nicht treffen.
 */
export function schrittId(projektId: string, schritt: Schrittart): string {
  const index = SCHRITTE.findIndex((s) => s.schritt === schritt);
  const teile = projektId.split('-');
  if (teile.length !== 5 || index < 0) throw new Error(`Keine gültige Projektkennung: ${projektId}`);
  return [teile[0], teile[1], `a00${index}`, teile[3], teile[4]].join('-');
}

/** „vor 3 Tagen“, „heute“, „in 2 Tagen“ — für die Erinnerung. */
export function faelligText(s: Schrittstand, heute: Date): string | null {
  if (!s.erinnernAm) return null;
  const tage = tageZwischen(tag(heute), s.erinnernAm);
  if (tage === 0) return 'fällig heute';
  if (tage < 0) return `fällig seit ${-tage} Tag${tage === -1 ? '' : 'en'}`;
  return `Erinnerung in ${tage} Tag${tage === 1 ? '' : 'en'}`;
}
